/* Every graphic in the material, generated.

   Geometry only, colours from the CSS tokens, so print and screen agree and
   there is no image file anywhere. The theory carries eleven of these and
   they are the point of the slides they sit on.

   Strings rather than JSX, for the same reason as lib/covers.ts: the same code
   renders into a React tree, into headless Chrome for a PDF, and into anything
   else that takes markup.

   OWNED BY ITEM 8 OF docs/plan/00-prestavba-2026-08.md. */

/* The drawing code below indexes arrays it has just built, so the element is
   always there. Saying so once beats a non-null assertion on every coordinate. */
function at<T>(a: readonly T[], i: number): T { return a[i] as T; }

type Opts = Record<string, any>;

let INK = "var(--ink)", RED = "var(--red)", MUTED = "var(--ink-3)";
let PASTEL = ["var(--p-peach)", "var(--p-sage)", "var(--p-sky)", "var(--p-sand)", "var(--p-mauve)", "var(--p-slate)"];

function svg(w: number, h: number, body: string, extra?: string): string {
  return '<svg viewBox="0 0 ' + w + " " + h + '" xmlns="http://www.w3.org/2000/svg" '
    + 'role="img" ' + (extra || "") + ">"
    + '<defs><marker id="ar" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">'
    + '<path d="M0 0 L10 5 L0 10 z" fill="' + INK + '"/></marker>'
    + '<marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">'
    + '<path d="M0 0 L10 5 L0 10 z" fill="' + RED + '"/></marker></defs>'
    + body + "</svg>";
}
function esc(s: unknown): string {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function txt(x: number, y: number, s: unknown, o?: Opts): string {
  o = o || {};
  return '<text x="' + x + '" y="' + y + '" fill="' + (o.fill || INK) + '" '
    + 'font-family="' + (o.serif ? "var(--serif)" : "var(--sans)") + '" '
    + 'font-size="' + (o.size || 14) + '" font-weight="' + (o.weight || 600) + '" '
    + 'text-anchor="' + (o.anchor || "middle") + '"'
    + (o.spacing ? ' letter-spacing="' + o.spacing + '"' : "")
    + ">" + esc(s) + "</text>";
}
function rect(x: number, y: number, w: number, h: number, o?: Opts): string {
  o = o || {};
  /* nothing in this material has a sharp corner; SVG clamps rx on thin bars */
  return '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + (o.r == null ? 6 : o.r)
    + '" fill="' + (o.fill || "none") + '" stroke="' + (o.stroke || INK) + '" stroke-width="'
    + (o.sw == null ? 1.5 : o.sw) + '"' + (o.dash ? ' stroke-dasharray="' + o.dash + '"' : "") + "/>";
}
function line(x1: number, y1: number, x2: number, y2: number, o?: Opts): string {
  o = o || {};
  return '<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2
    + '" stroke="' + (o.stroke || INK) + '" stroke-width="' + (o.sw || 1.5) + '"'
    + (o.dash ? ' stroke-dasharray="' + o.dash + '"' : "")
    + (o.arrow ? ' marker-end="url(#' + (o.arrow === "red" ? "arr" : "ar") + ')"' : "") + "/>";
}
/* text wrapped into <= n lines inside a box */
function wrap(x: number, y: number, s: string, width: number, o?: Opts): string {
  o = o || {};
  let size = o.size || 13, per = Math.floor(width / (size * 0.5));
  let words = String(s).split(" "), lines = [], cur = "";
  words.forEach(function (w: any) {
    if ((cur + " " + w).trim().length > per) { lines.push(cur.trim()); cur = w; }
    else cur += " " + w;
  });
  if (cur.trim()) lines.push(cur.trim());
  return lines.map(function (l: any, i: any) {
    return txt(x, y + i * (size * 1.35), l, o);
  }).join("");
}

export const FIGURES: Record<string, (o?: Opts) => string> = {

  /* prompt -> model -> modalities */
  pipeline: function () {
    let outs = ["Text", "Obraz", "Zvuk", "Video", "Kód"];
    let body = rect(20, 90, 190, 80, { stroke: INK })
      + txt(115, 124, "PROMPT", { size: 17, weight: 900, spacing: "1.5" })
      + txt(115, 148, "váš požadavek běžným jazykem", { size: 12, weight: 400, fill: MUTED, serif: true })
      + line(215, 130, 285, 130, { arrow: 1 })
      + rect(290, 70, 200, 120, { stroke: RED, sw: 2.5, fill: "var(--paper-2)" })
      + txt(390, 120, "MODEL", { size: 20, weight: 900, spacing: "2", fill: RED })
      + txt(390, 145, "LLM / multimodální", { size: 12, weight: 400, fill: MUTED, serif: true })
      + txt(390, 165, "predikce dalšího tokenu", { size: 12, weight: 400, fill: MUTED, serif: true });
    outs.forEach(function (o: any, i: any) {
      let y = 30 + i * 50;
      body += line(495, 130, 560, y + 20, { stroke: MUTED, sw: 1, arrow: 1 })
        + rect(565, y, 110, 40, { fill: PASTEL[i], stroke: INK, sw: 1 })
        + txt(620, y + 26, o, { size: 14 });
    });
    return svg(700, 280, body);
  },

  /* one number per word does not work; vectors do */
  vectors: function () {
    /* the slide is already titled "Proč jedno číslo na slovo nestačí" — the
       label only has to name the half of the drawing it sits above */
    let body = txt(40, 34, "Jedno číslo", { anchor: "start", size: 15, weight: 700 })
      + txt(40, 62, "Fialová = 99  ·  Ovoce = 42  →  141", { anchor: "start", size: 15, weight: 400, serif: true })
      + txt(40, 88, "Muž = 69  ·  Žena = 72  →  141", { anchor: "start", size: 15, weight: 400, serif: true })
      + txt(40, 116, "Stejný součet, jiný význam. Nefunguje.", { anchor: "start", size: 13, weight: 400, fill: RED, serif: true })
      + line(40, 140, 660, 140, { stroke: "var(--rule)", sw: 1 })
      + txt(40, 176, "Vektor: seznam čísel, každé pro jinou vlastnost", { anchor: "start", size: 15, weight: 700 });
    let dims = ["živé", "jedlé", "barva", "velikost", "…", "1024"];
    dims.forEach(function (d: any, i: any) {
      let x = 40 + i * 105;
      body += rect(x, 196, 95, 54, { fill: PASTEL[i], stroke: INK, sw: 1 })
        + txt(x + 47, 220, d, { size: 12, fill: MUTED })
        + txt(x + 47, 240, [0.02, 0.91, 0.77, 0.14, "…", 0.38][i], { size: 15, weight: 700, serif: true });
    });
    body += txt(40, 282, "Součet po složkách dává smysl — přesně jako sčítání barev v RGB.",
      { anchor: "start", size: 13, weight: 400, fill: MUTED, serif: true });
    return svg(700, 300, body);
  },

  /* RGB proof */
  rgb: function () {
    function swatch(x: any, fill: any, label: any, vec: any) {
      return rect(x, 30, 120, 80, { fill: fill, stroke: INK, sw: 1.5 })
        + txt(x + 60, 132, label, { size: 15, weight: 700 })
        + txt(x + 60, 154, vec, { size: 12, weight: 400, fill: MUTED, serif: true });
    }
    let body = swatch(30, "#e02020", "Červená", "[255, 0, 0]")
      + txt(180, 78, "+", { size: 26, weight: 900 })
      + swatch(200, "#1f9d3a", "Zelená", "[0, 255, 0]")
      + txt(350, 78, "=", { size: 26, weight: 900 })
      + swatch(370, "#f2d024", "Žlutá", "[255, 255, 0]")
      + txt(540, 62, "Sčítáte složku", { anchor: "start", size: 14, weight: 400, serif: true })
      + txt(540, 84, "po složce.", { anchor: "start", size: 14, weight: 400, serif: true })
      + txt(540, 112, "Se slovy je to stejné,", { anchor: "start", size: 14, weight: 400, fill: MUTED, serif: true })
      + txt(540, 134, "jen rozměrů je tisíc.", { anchor: "start", size: 14, weight: 400, fill: MUTED, serif: true });
    return svg(700, 180, body);
  },

  /* A vector with components anyone can feel. Four axes of taste, three
     dishes: two land on top of each other, the third nowhere near. The point
     is not the food — it is that a list of numbers already means "how alike". */
  flavors: function () {
    let cols = ["sladkost", "slanost", "pálivost", "tuhost"];
    let rows = [
      ["Med", [9, 0, 0, 1]],
      ["Marmeláda", [8, 0, 0, 2]],
      ["Guláš", [1, 6, 5, 4]],
    ];
    let X = 190, W = 84, STEP = 100, Y0 = 78, RH = 52;

    let body = txt(20, 28, "jídlo [ sladkost, slanost, pálivost, tuhost ]",
      { anchor: "start", size: 16, weight: 700 });
    cols.forEach(function (c: any, i: any) {
      body += txt(X + i * STEP + W / 2, 62, c, { size: 11, weight: 700, fill: MUTED, spacing: "0.6" });
    });
    rows.forEach(function (r: any, ri: any) {
      let y = Y0 + ri * RH;
      body += txt(20, y + 30, r[0], { anchor: "start", size: 16, weight: 700 });
      r[1].forEach(function (v: any, i: any) {
        let x = X + i * STEP;
        body += rect(x, y + 10, W, 28, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
          + (v ? rect(x, y + 10, Math.max(10, W * v / 9), 28, { fill: PASTEL[i], stroke: "none", sw: 0 }) : "")
          + txt(x + W - 8, y + 30, v, { anchor: "end", size: 14, weight: 700, serif: true });
      });
    });

    /* the whole argument in two brackets: same numbers, same shelf */
    body += line(596, Y0 + 12, 596, Y0 + RH + 36, { stroke: RED, sw: 2 })
      + txt(608, Y0 + RH / 2 + 22, "blízko", { anchor: "start", size: 13, weight: 700, fill: RED })
      + line(596, Y0 + 2 * RH + 12, 596, Y0 + 2 * RH + 36, { stroke: MUTED, sw: 2 })
      + txt(608, Y0 + 2 * RH + 30, "daleko", { anchor: "start", size: 13, weight: 700, fill: MUTED });

    body += txt(20, 268, "Podobnost dvou jídel je vzdálenost dvou seznamů čísel. Nic víc v tom není.",
      { anchor: "start", size: 13, weight: 400, fill: MUTED, serif: true });
    return svg(700, 286, body);
  },

  /* token by token */
  /* Token by token: the context is re-read whole for every single token.
     The sequence builds itself once (the tok-in/tok-out keyframes in
     styles/deck.css), then holds. */
  tokens: function () {
    let toks = ["Co", "je", "šeptem,", "to", "je", "čertem."];
    let T = 0.85;                       /* seconds one token takes on screen */
    let w = function (s: any) { return s.length * 8.2 + 18; };

    let body = txt(20, 16, "KONTEXT — ČTE SE CELÝ ZNOVU PRO KAŽDÝ TOKEN",
      { anchor: "start", size: 10, weight: 700, fill: MUTED, spacing: 1.4 });

    /* the two parts of the context that never change */
    [["SYSTÉMOVÝ PROMPT", "Piš česky. Odpovídej stručně.", 20],
     ["ZADÁNÍ", "Vytvoř mi české přísloví.", 360]].forEach(function (c: any) {
      body += rect(c[2], 26, 320, 56, { fill: "none", stroke: MUTED, sw: 1 })
        + txt(c[2] + 12, 44, c[0], { anchor: "start", size: 10, weight: 700, fill: MUTED, spacing: 1.2 })
        + txt(c[2] + 12, 68, c[1], { anchor: "start", size: 14, weight: 400, serif: true });
    });

    /* the part that grows: one chip per token already written */
    body += rect(20, 92, 660, 52, { fill: "none", stroke: INK, sw: 1.5, dash: "5 4" })
      + txt(32, 110, "DOSUD VYGENEROVANÉ TOKENY", { anchor: "start", size: 10, weight: 700, fill: MUTED, spacing: 1.2 });
    let x = 32;
    toks.forEach(function (s: any, i: any) {
      let cw = w(s);
      body += '<g class="tok-in" style="animation-delay:' + ((i + 1) * T).toFixed(2) + 's">'
        + rect(x, 116, cw, 24, { fill: PASTEL[2], stroke: INK, sw: 1 })
        + txt(x + cw / 2, 133, s, { size: 14, weight: 700, serif: true })
        + "</g>";
      x += cw + 8;
    });

    body += line(350, 150, 350, 176, { arrow: 1 })
      + rect(290, 178, 120, 38, { fill: "var(--paper-2)", stroke: RED, sw: 2.5 })
      + txt(350, 202, "MODEL", { size: 14, weight: 700, fill: RED, spacing: 1.2 })
      + line(350, 220, 350, 244, { arrow: "red" })
      + txt(364, 238, "jeden token", { anchor: "start", size: 11, weight: 400, fill: MUTED, serif: true });

    /* the token just produced, one at a time */
    toks.forEach(function (s: any, i: any) {
      let cw = w(s);
      body += '<g class="tok-out' + (i === toks.length - 1 ? " tok-last" : "")
        + '" style="animation-delay:' + (i * T).toFixed(2) + 's">'
        + rect(350 - cw / 2, 248, cw, 26, { fill: "var(--paper-2)", stroke: RED, sw: 1.5 })
        + txt(350, 266, s, { size: 14, weight: 700, fill: RED, serif: true })
        + "</g>";
    });

    /* the loop starts clear of the widest chip and the label sits inside the
       bend, where nothing else is drawn */
    body += '<path d="M400 261 C 560 261, 640 250, 640 160" fill="none" stroke="' + RED
      + '" stroke-width="1.5" stroke-dasharray="4 4" marker-end="url(#arr)"/>'
      + txt(608, 204, "přidá se zpět do kontextu",
        { anchor: "end", size: 11, weight: 400, fill: RED, serif: true });

    return svg(700, 286, body);
  },

  /* agentic loop */
  agent: function () {
    let nodes = [
      ["Uživatel", "zadá úkol", 40],
      ["Agent", "rozloží na kroky", 210],
      ["Nástroje", "web, soubory, kód", 380],
      ["Výsledek", "hotový výstup", 550],
    ];
    let body = "";
    nodes.forEach(function (n: any, i: any) {
      body += rect(n[2], 60, 120, 72, { fill: i === 1 ? "var(--paper-2)" : "none", stroke: i === 1 ? RED : INK, sw: i === 1 ? 2.5 : 1.5 })
        + txt(n[2] + 60, 90, n[0], { size: 15, weight: 700, fill: i === 1 ? RED : INK })
        + txt(n[2] + 60, 112, n[1], { size: 11, weight: 400, fill: MUTED, serif: true });
      if (i < 3) body += line(n[2] + 122, 96, n[2] + 168, 96, { arrow: 1 });
    });
    /* the arc bottoms out around y=177, so the caption sits below it, not on it */
    body += '<path d="M440 140 C 440 190, 270 190, 270 140" fill="none" stroke="' + RED
      + '" stroke-width="1.5" stroke-dasharray="4 4" marker-end="url(#arr)"/>'
      + txt(355, 198, "opakuje, dokud není hotovo", { size: 12, weight: 400, fill: RED, serif: true });
    return svg(700, 212, body);
  },

  /* four core elements of a linear prompt */
  promptcore: function () {
    let items = [
      ["Role", "Kým má model být.", "Jsi zkušený učitel dějepisu na druhém stupni."],
      ["Kontext", "V jaké situaci jste.", "Učím 8. ročník, 45 minut, třída 24 žáků."],
      ["Úkol", "Co přesně chcete.", "Vytvoř pracovní list s deseti úlohami."],
      ["Formát", "Jak má výstup vypadat.", "Tabulka, jedna strana A4, bez obrázků."],
    ];
    let body = "";
    items.forEach(function (it: any, i: any) {
      let y = 10 + i * 78;
      body += rect(20, y, 660, 66, { fill: i % 2 ? "none" : "var(--paper-2)", stroke: "var(--rule)", sw: 1 })
        + rect(20, y, 6, 66, { fill: RED, stroke: "none", sw: 0 })
        + txt(44, y + 28, it[0], { anchor: "start", size: 16, weight: 900 })
        + txt(44, y + 50, it[1], { anchor: "start", size: 12, weight: 400, fill: MUTED, serif: true })
        + txt(250, y + 40, it[2], { anchor: "start", size: 14, weight: 400, serif: true });
    });
    return svg(700, 320, body);
  },

  /* three prompting strategies, scaled by how much the teacher grows */
  strategies: function () {
    let s = [
      ["Lineární", 35, "Vše naráz v jednom promptu. Vyžaduje, abyste výsledek znali předem."],
      ["Iterativní", 50, "Krátký start, dialog, postupné zpřesňování. Nejběžnější postup."],
      ["Analytická", 85, "Necháte model navrhnout kritéria a postup. Vy rozhodujete."],
    ];
    let body = txt(20, 24, "Index vlastního rozvoje", { anchor: "start", size: 12, weight: 600, fill: MUTED, spacing: "1.2" });
    s.forEach(function (it: any, i: any) {
      let y = 50 + i * 86;
      body += txt(20, y + 20, it[0], { anchor: "start", size: 18, weight: 900 })
        + rect(20, y + 34, 640, 18, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
        + rect(20, y + 34, 640 * it[1] / 100, 18, { fill: i === 2 ? RED : INK, stroke: "none", sw: 0 })
        + txt(30 + 640 * it[1] / 100, y + 48, it[1] + " %", { anchor: "start", size: 13, weight: 700 })
        + txt(20, y + 72, it[2], { anchor: "start", size: 13, weight: 400, fill: MUTED, serif: true });
    });
    return svg(700, 310, body);
  },

  /* the five planning steps as one arc */
  fivesteps: function () {
    let s = ["Destination", "Baseline", "Sequencing", "Cut-list", "Milestones"];
    let cz = ["Cíl období", "Výchozí stav", "Pořadí", "Vyřazené", "Důkaz"];
    let body = line(40, 120, 660, 120, { stroke: "var(--rule)", sw: 1 });
    s.forEach(function (n: any, i: any) {
      let x = 60 + i * 145;
      body += '<circle cx="' + x + '" cy="120" r="18" fill="' + (i === 0 || i === 4 ? RED : "var(--paper)")
        + '" stroke="' + INK + '" stroke-width="1.5"/>'
        + txt(x, 126, String(i + 1), { size: 15, weight: 900, fill: i === 0 || i === 4 ? "var(--paper)" : INK })
        + txt(x, 84, n, { size: 14, weight: 900 })
        + txt(x, 160, cz[i], { size: 13, weight: 400, fill: MUTED, serif: true });
    });
    return svg(700, 190, body);
  },

  /* ŠVP funnel down to a single lesson and its materials */
  funnel: function () {
    let rows = [
      ["ŠVP", "závazný rámec předmětu", 0],
      ["Plán období", "pololetí nebo rok, pět kroků", 1],
      ["Rozpad na hodiny", "podle časové dotace", 2],
      ["Jedna hodina", "název, cíl, důkaz", 3],
      ["Materiály", "pro studenty i pro učitele", 4],
    ];
    let body = "";
    rows.forEach(function (r: any, i: any) {
      let inset = i * 46, y = 14 + i * 62;
      body += rect(20 + inset, y, 660 - inset * 2, 50, { fill: PASTEL[i], stroke: INK, sw: 1.5 })
        + txt(350, y + 24, r[0], { size: 16, weight: 900 })
        + txt(350, y + 42, r[1], { size: 12, weight: 400, fill: "var(--ink-2)", serif: true });
      if (i < rows.length - 1) body += line(350, y + 50, 350, y + 62, { arrow: "red", stroke: RED });
    });
    return svg(700, 340, body);
  },

  /* the one-go run: six phases of a single sitting, one conversation */
  fullrun: function () {
    let ph = [
      ["Soupis", "co k hodině vznikne"],
      ["Materiály", "pět kusů pro studenty"],
      ["Podklady", "linka, řešení, fakta"],
      ["Aplikace", "jedna až tři stránky"],
      ["Soubory", "hotové .html na disku"],
      ["Hodina", "rozvržení a záloha"],
    ];
    let body = rect(20, 10, 660, 38, { fill: "var(--paper-2)", stroke: RED, sw: 2.5 })
      + txt(350, 35, "JEDNA KONVERZACE, JEDNO SEZENÍ",
            { size: 15, weight: 900, spacing: "1.5", fill: RED })
      + line(66, 132, 634, 132, { stroke: RED, sw: 3 });
    ph.forEach(function (p: any, i: any) {
      let cx = 66 + i * 113.6;
      body += rect(cx - 52, 70, 104, 44, { fill: PASTEL[i], stroke: INK, sw: 1.5 })
        + txt(cx, 86, String(i + 1), { size: 10, weight: 900, fill: "var(--ink-3)" })
        + txt(cx, 104, p[0], { size: 14, weight: 900 })
        + line(cx, 114, cx, 126, { stroke: "var(--rule)", sw: 1 })
        + '<circle cx="' + cx + '" cy="132" r="6" fill="' + RED + '"/>'
        + wrap(cx, 158, p[1], 104, { size: 11, weight: 400, serif: true, fill: "var(--ink-2)" });
    });
    body += txt(350, 206, "Každá fáze staví na odpovědi předchozí. Nic se nezadává dvakrát.",
      { size: 13, weight: 400, fill: MUTED, serif: true });
    return svg(700, 222, body);
  },

  /* multi-speed materials: one topic, several ways in */
  spectrum: function () {
    let ways = [
      ["Vidět", "schéma, časová osa, obrázek"],
      ["Osahat", "pokus, karty, model z papíru"],
      ["Slyšet", "výklad, podcast, rozhovor"],
      ["Vidět druhé", "ukázkové řešení, video postupu"],
      ["Zkusit sám", "úloha s okamžitou zpětnou vazbou"],
    ];
    let body = rect(250, 10, 200, 46, { fill: "var(--paper-2)", stroke: RED, sw: 2.5 })
      + txt(350, 39, "JEDNO TÉMA", { size: 16, weight: 900, spacing: "1.5", fill: RED });
    ways.forEach(function (w: any, i: any) {
      let x = 20 + i * 133, y = 120;
      body += line(350, 58, x + 56, y - 8, { stroke: "var(--rule)", sw: 1 })
        + rect(x, y, 112, 76, { fill: PASTEL[i], stroke: INK, sw: 1 })
        + txt(x + 56, y + 28, w[0], { size: 14, weight: 900 })
        + wrap(x + 56, y + 48, w[1], 100, { size: 11, weight: 400, serif: true, fill: "var(--ink-2)" });
    });
    body += txt(350, 236, "Student si vybere cestu. Cíl hodiny zůstává stejný pro všechny.",
      { size: 13, weight: 400, fill: MUTED, serif: true });
    return svg(700, 256, body);
  },

  /* verification loop: never hand over an unchecked output */
  verify: function () {
    let body = rect(20, 40, 150, 66, { stroke: INK })
      + txt(95, 70, "Výstup modelu", { size: 14, weight: 700 })
      + txt(95, 90, "znějící přesvědčivě", { size: 11, weight: 400, fill: MUTED, serif: true })
      + line(172, 73, 218, 73, { arrow: 1 })
      + '<path d="M300 30 L370 73 L300 116 L230 73 Z" fill="var(--paper-2)" stroke="' + RED + '" stroke-width="2.5"/>'
      + txt(300, 70, "Ověření", { size: 14, weight: 900, fill: RED })
      + txt(300, 88, "zdroj, číslo, jméno", { size: 10, weight: 400, fill: MUTED, serif: true })
      + line(372, 73, 418, 73, { arrow: 1 })
      + rect(420, 40, 150, 66, { stroke: INK })
      + txt(495, 70, "Materiál pro žáky", { size: 14, weight: 700 })
      + txt(495, 90, "podepsaný učitelem", { size: 11, weight: 400, fill: MUTED, serif: true })
      + '<path d="M300 118 C 300 158, 95 158, 95 112" fill="none" stroke="' + RED
      + '" stroke-width="1.5" stroke-dasharray="4 4" marker-end="url(#arr)"/>'
      + txt(198, 186, "neprošlo — zpět s upřesněním", { size: 12, weight: 400, fill: RED, serif: true });
    return svg(700, 206, body);
  },

  /* the same request three times: an image is a sample, an application is code */
  determinism: function () {
    /* one thumbnail of a generated picture — deliberately different each time */
    function picture(x: any, i: any) {
      let c = at([[30, 150, 15], [60, 162, 10], [45, 144, 19]], i);
      return rect(x, 122, 90, 62, { fill: PASTEL[i], stroke: INK, sw: 1 })
        + '<circle cx="' + (x + c[0]) + '" cy="' + c[1] + '" r="' + c[2]
        + '" fill="var(--paper-2)" stroke="' + INK + '" stroke-width="1"/>';
    }
    /* one thumbnail of a generated page — identical every time */
    function page(x: any) {
      return rect(x, 122, 90, 62, { fill: "var(--paper-2)", stroke: INK, sw: 1 })
        + rect(x + 10, 130, 46, 8, { fill: RED, stroke: "none", sw: 0 })
        + rect(x + 10, 144, 70, 5, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
        + rect(x + 10, 153, 56, 5, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
        + rect(x + 10, 165, 40, 12, { fill: INK, stroke: "none", sw: 0 });
    }

    let body = rect(250, 8, 200, 44, { fill: "var(--paper-2)", stroke: INK, sw: 2 })
      + txt(350, 30, "TOTÉŽ ZADÁNÍ", { size: 14, weight: 900, spacing: "1.5" })
      + txt(350, 46, "třikrát za sebou", { size: 11, weight: 400, fill: MUTED, serif: true })
      + line(320, 54, 190, 88, { stroke: MUTED, sw: 1, arrow: 1 })
      + line(380, 54, 510, 88, { stroke: MUTED, sw: 1, arrow: 1 })
      + txt(180, 108, "GENEROVANÝ OBRÁZEK", { size: 13, weight: 900, spacing: ".8" })
      + txt(520, 108, "GENEROVANÁ APLIKACE", { size: 13, weight: 900, spacing: ".8", fill: RED });

    [35, 135, 235].forEach(function (x: any, i: any) { body += picture(x, i); });
    [375, 475, 575].forEach(function (x: any) { body += page(x); });

    body += txt(180, 206, "Pokaždé jiný výsledek.", { size: 13, weight: 700 })
      + txt(180, 226, "Vzorek z rozdělení — nemusí vyjít.", { size: 12, weight: 400, fill: MUTED, serif: true })
      + txt(520, 206, "Pokaždé stejný výsledek.", { size: 13, weight: 700, fill: RED })
      + txt(520, 226, "Kód se vykoná pokaždé stejně.", { size: 12, weight: 400, fill: MUTED, serif: true })
      + line(350, 96, 350, 236, { stroke: "var(--rule)", sw: 1, dash: "3 5" });
    return svg(700, 246, body);
  },

  /* four parts of one page, and what each of them is responsible for */
  layers: function () {
    let rows = [
      ["HTML", "Struktura", "co na stránce je"],
      ["CSS", "Vzhled", "jak to vypadá"],
      ["JavaScript", "Chování", "co se stane po kliknutí"],
      ["SVG", "Kresba", "graf, schéma, model"],
    ];
    let body = "";
    rows.forEach(function (r: any, i: any) {
      let y = 26 + i * 64;
      body += rect(20, y, 350, 54, { fill: "var(--paper-2)", stroke: "var(--rule)", sw: 1 })
        + rect(20, y, 9, 54, { fill: PASTEL[i], stroke: "none", sw: 0 })
        + txt(46, y + 24, r[0], { anchor: "start", size: 15, weight: 900 })
        + txt(46, y + 44, r[2], { anchor: "start", size: 11, weight: 400, fill: MUTED, serif: true })
        + txt(356, y + 32, r[1], { anchor: "end", size: 13, weight: 700, fill: "var(--ink-2)" });
    });

    /* the rendered page, with a colour chip on the part each layer produces */
    function chip(x: any, y: any, i: any) {
      return rect(x, y, 11, 11, { fill: PASTEL[i], stroke: INK, sw: .8, r: 1 });
    }
    /* chips live in a gutter down the left edge of the mock, so they never
       sit on top of the element they label */
    body += line(378, 152, 424, 152, { arrow: "red", stroke: RED })
      + rect(430, 26, 250, 244, { fill: "var(--paper-2)", stroke: INK, sw: 2 })
      + rect(430, 26, 250, 20, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
      + txt(555, 40, "jeden soubor, otevře se dvojklikem", { size: 10, weight: 400, fill: MUTED, serif: true })
      + rect(462, 62, 140, 16, { fill: RED, stroke: "none", sw: 0 }) + chip(438, 64, 1)
      + rect(462, 92, 196, 6, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
      + rect(462, 104, 160, 6, { fill: "var(--paper-3)", stroke: "none", sw: 0 }) + chip(438, 94, 0)
      + rect(462, 124, 196, 82, { fill: "none", stroke: "var(--rule)", sw: 1 })
      + '<polyline points="472,192 504,170 536,180 568,142 600,152 648,134" fill="none" stroke="'
        + RED + '" stroke-width="2"/>' + chip(438, 128, 3)
      + rect(462, 222, 122, 30, { fill: INK, stroke: "none", sw: 0 })
      + txt(523, 242, "Zkontrolovat", { size: 12, weight: 700, fill: "var(--paper)" })
      + chip(438, 232, 2);
    return svg(700, 282, body);
  },

  /* idea -> description -> file -> try it -> fix -> lesson */
  buildloop: function () {
    let nodes = [
      ["Nápad", "co má student umět"],
      ["Popis", "obrazovka a reakce"],
      ["Soubor", "otevře se dvojklikem"],
      ["Zkouška", "projdete jako student"],
      ["Hodina", "materiál je hotový"],
    ];
    let body = "";
    nodes.forEach(function (n: any, i: any) {
      let x = 20 + i * 136, on = i === 3;
      body += rect(x, 40, 116, 72, {
          fill: on ? "var(--paper-2)" : "none", stroke: on ? RED : INK, sw: on ? 2.5 : 1.5 })
        + txt(x + 58, 70, n[0], { size: 15, weight: 700, fill: on ? RED : INK })
        + wrap(x + 58, 90, n[1], 104, { size: 11, weight: 400, fill: MUTED, serif: true });
      if (i < nodes.length - 1) body += line(x + 118, 76, x + 134, 76, { arrow: 1 });
    });
    body += '<path d="M486 120 C 486 172, 214 172, 214 120" fill="none" stroke="' + RED
      + '" stroke-width="1.5" stroke-dasharray="4 4" marker-end="url(#arr)"/>'
      + txt(350, 186, "závadu popíšete běžnou větou, oprava se vrátí za pár vteřin",
          { size: 12, weight: 400, fill: RED, serif: true });
    return svg(700, 200, body);
  },

  /* the step an expert takes in one go, and the same distance for a beginner */
  granularity: function () {
    function dot(x: any, y: any, filled: any) {
      return '<circle cx="' + x + '" cy="' + y + '" r="7" fill="'
        + (filled ? INK : "var(--paper)") + '" stroke="' + INK + '" stroke-width="1.5"/>';
    }
    let body = txt(20, 26, "JEDEN KROK ODBORNÍKA", { anchor: "start", size: 12, weight: 900, spacing: "1" })
      + line(70, 74, 626, 74, { arrow: "red", stroke: RED, sw: 2 })
      + dot(60, 74, 1) + dot(640, 74, 0)
      + txt(60, 100, "co student umí", { anchor: "start", size: 11, weight: 400, fill: MUTED, serif: true })
      + txt(640, 100, "cíl hodiny", { anchor: "end", size: 11, weight: 400, fill: MUTED, serif: true })
      + txt(350, 58, "vzdálenost, kterou už odborník nevidí", { size: 12, weight: 400, fill: RED, serif: true })
      + line(20, 126, 680, 126, { stroke: "var(--rule)", sw: 1 })
      + txt(20, 158, "TÁŽ VZDÁLENOST PRO ZAČÁTEČNÍKA", { anchor: "start", size: 12, weight: 900, spacing: "1" });

    /* six dots spanning exactly the same 60 → 640 as the single step above */
    for (let i = 0; i < 5; i++) {
      let x = 60 + i * 116;
      body += line(x + 10, 206, x + 106, 206, { arrow: 1 })
        + txt(x + 58, 194, String(i + 1), { size: 12, weight: 700, fill: MUTED });
    }
    for (let j = 0; j < 6; j++) body += dot(60 + j * 116, 206, j === 0);
    body += txt(350, 244, "Kroky rozloží model. Určit, ve kterém z nich student vypadne, musí učitel.",
      { size: 13, weight: 400, fill: MUTED, serif: true });
    return svg(700, 258, body);
  },

  /* what the model produces unasked, next to what a style block produces */
  breathe: function () {
    /* crammed: everything boxed, nothing has room */
    function dense(x: any) {
      let b = rect(x, 40, 320, 226, { fill: "var(--paper-2)", stroke: INK, sw: 1.5 });
      let rows = [[0, 3], [1, 2], [2, 3], [3, 2]];
      rows.forEach(function (r: any) {
        let y = 52 + r[0] * 54, w = (300 - (r[1] - 1) * 8) / r[1];
        for (let i = 0; i < r[1]; i++) {
          let bx = x + 10 + i * (w + 8);
          b += rect(bx, y, w, 46, { fill: "none", stroke: MUTED, sw: 1 });
          for (let l = 0; l < 4; l++) {
            b += rect(bx + 5, y + 7 + l * 9, w - 10 - (l === 3 ? 18 : 0), 4,
                      { fill: PASTEL[(r[0] + i + l) % 6], stroke: "none", sw: 0 });
          }
        }
      });
      return b;
    }
    /* airy: one idea, generous margins, a single accent */
    function airy(x: any) {
      return rect(x, 40, 320, 226, { fill: "var(--paper-2)", stroke: INK, sw: 1.5 })
        + rect(x + 40, 74, 150, 12, { fill: INK, stroke: "none", sw: 0 })
        + rect(x + 40, 112, 240, 5, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
        + rect(x + 40, 126, 210, 5, { fill: "var(--paper-3)", stroke: "none", sw: 0 })
        + '<polyline points="' + (x + 40) + ',206 ' + (x + 96) + ',186 '
          + (x + 152) + ',192 ' + (x + 208) + ',158 ' + (x + 264) + ',166" '
          + 'fill="none" stroke="' + RED + '" stroke-width="2.5"/>'
        + rect(x + 40, 226, 84, 14, { fill: "none", stroke: MUTED, sw: 1 });
    }
    let body = txt(180, 28, "CO VZNIKNE BEZ ZADÁNÍ STYLU", { size: 13, weight: 900, spacing: ".8" })
      + txt(520, 28, "SE STYLOVÝM BLOKEM", { size: 13, weight: 900, spacing: ".8", fill: RED })
      + dense(20) + airy(360)
      + txt(180, 288, "Vše orámované, vše stejně důležité.", { size: 12, weight: 400, fill: MUTED, serif: true })
      + txt(520, 288, "Jedna myšlenka, místo kolem, jeden akcent.", { size: 12, weight: 400, fill: MUTED, serif: true });
    return svg(700, 300, body);
  },

  /* four ways to lay the same material out */
  formats: function () {
    let W = 150, H = 104, Y = 34;
    function frame(x: any) { return rect(x, Y, W, H, { fill: "var(--paper-2)", stroke: INK, sw: 1.5 }); }
    function bar(x: any, y: any, w: any, h: any, c: any) { return rect(x, y, w, h, { fill: c, stroke: "none", sw: 0 }); }
    let G = "var(--paper-3)";

    /* 1 — two columns, the right one develops as you scroll */
    let a = 20, body = frame(a)
      + bar(a + 10, Y + 12, 56, 5, G) + bar(a + 10, Y + 22, 56, 5, G)
      + bar(a + 10, Y + 32, 46, 5, G) + bar(a + 10, Y + 52, 56, 5, G)
      + bar(a + 10, Y + 62, 40, 5, G)
      + rect(a + 78, Y + 12, 62, 80, { fill: "none", stroke: MUTED, sw: 1 })
      + '<polyline points="' + (a + 84) + ',' + (Y + 80) + ' ' + (a + 104) + ',' + (Y + 58)
        + ' ' + (a + 124) + ',' + (Y + 66) + '" fill="none" stroke="' + RED + '" stroke-width="2"/>'
      + line(a + 109, Y + 96, a + 109, Y + 112, { stroke: MUTED, sw: 1, arrow: 1 });

    /* 2 — one screen at a time */
    let b = 195; body += frame(b)
      + bar(b + 34, Y + 26, 82, 9, INK)
      + bar(b + 34, Y + 46, 82, 5, G) + bar(b + 44, Y + 56, 62, 5, G)
      + txt(b + 18, Y + 84, "‹", { size: 20, weight: 700, fill: RED })
      + txt(b + 132, Y + 84, "›", { size: 20, weight: 700, fill: RED });

    /* 3 — steps that unlock one after another */
    let c = 370; body += frame(c);
    for (let i = 0; i < 4; i++) {
      let on = i === 1, y = Y + 12 + i * 22;
      body += rect(c + 12, y, 126, 16, { fill: on ? "var(--paper)" : "none", stroke: on ? RED : MUTED, sw: on ? 2 : 1 })
        + bar(c + 18, y + 6, on ? 70 : 44, 4, on ? RED : G);
    }

    /* 4 — one instrument the student drives */
    let d = 545; body += frame(d)
      + rect(d + 12, Y + 10, 126, 62, { fill: "none", stroke: MUTED, sw: 1 })
      + '<circle cx="' + (d + 75) + '" cy="' + (Y + 41) + '" r="22" fill="none" stroke="'
        + RED + '" stroke-width="2.5"/>'
      + line(d + 75, Y + 41, d + 95, Y + 29, { stroke: INK, sw: 1.5 })
      + line(d + 20, Y + 86, d + 130, Y + 86, { stroke: G, sw: 4 })
      + '<circle cx="' + (d + 96) + '" cy="' + (Y + 86) + '" r="6" fill="' + RED + '"/>';

    let names = [["Postupné odhalování", 95], ["Prezentace", 270], ["Krok za krokem", 445], ["Jeden nástroj", 620]];
    names.forEach(function (n: any) { body += txt(n[1], Y + H + 26, n[0], { size: 13, weight: 700 }); });
    return svg(700, 190, body);
  },

  /* two sets of tools, same destination */
  toolsets: function () {
    let body = rect(20, 20, 300, 110, { fill: "var(--paper-2)", stroke: INK })
      + txt(170, 48, "CHAT A KANCELÁŘ", { size: 14, weight: 900, spacing: "1" })
      + txt(170, 74, "Claude chat, Claude Cowork", { size: 13, weight: 400, serif: true })
      + txt(170, 96, "dokument, pracovní list, prezentace", { size: 12, weight: 400, fill: MUTED, serif: true })
      + rect(380, 20, 300, 110, { fill: "var(--paper-2)", stroke: RED, sw: 2.5 })
      + txt(530, 48, "KÓD A REPOZITÁŘ", { size: 14, weight: 900, spacing: "1", fill: RED })
      + txt(530, 74, "Claude Code, GitHub, nasazení", { size: 13, weight: 400, serif: true })
      + txt(530, 96, "interaktivní aplikace pro studenty", { size: 12, weight: 400, fill: MUTED, serif: true })
      + line(170, 132, 330, 186, { stroke: MUTED, sw: 1, arrow: 1 })
      + line(530, 132, 370, 186, { stroke: MUTED, sw: 1, arrow: 1 })
      + rect(200, 190, 300, 56, { stroke: INK, sw: 2 })
      + txt(350, 216, "Student rozumí a umí to použít", { size: 15, weight: 700 })
      + txt(350, 236, "stejný cíl, jiná sada nástrojů", { size: 11, weight: 400, fill: MUTED, serif: true });
    return svg(700, 260, body);
  },
};
